import { Autocomplete, Box, TextField } from "@mui/material";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getCountryCode } from "../../../../common/utils/countriesData";
import { selectSpot } from "../../../spots/spotsSlice";

export const GoogleMapsWorkspaceIdFinder = ({ onChange, id }) => {
  const [predictions, setPredictions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const places = useMapsLibrary("places");
  const spot = useSelector((state) => selectSpot(state, id));
  const countryCode = getCountryCode(spot.country);

  useEffect(() => {
    if (!places) return;
    const sessionToken = new places.AutocompleteSessionToken();

    if (inputValue) {
      function getBoundingBox(lat, lng, sizeKm = 15) {
        // Convert latitude to radians
        const latRad = (lat * Math.PI) / 180;

        // Half-size of the box (in km)
        const halfSize = sizeKm / 2;

        // 1° latitude in km (constant)
        const latDegreeKm = 111.32;

        // Calculate delta lat
        const deltaLat = halfSize / latDegreeKm;

        // 1° longitude in km (depends on latitude)
        const lngDegreeKm = 111.32 * Math.cos(latRad);

        // Calculate delta lng
        const deltaLng = halfSize / lngDegreeKm;

        return {
          north: lat + deltaLat,
          south: lat - deltaLat,
          east: lng + deltaLng,
          west: lng - deltaLng,
        };
      }

      const boundingBox = getBoundingBox(spot.latitude, spot.longitude, 35);
      const request = {
        input: `${inputValue}`,
        sessionToken: sessionToken,
        locationRestriction: boundingBox,
        language: "en-US",
        includedPrimaryTypes: ["establishment"],
      };

      const getSuggestions = async () => {
        try {
          const { suggestions: rawSuggestions } =
            await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              request
            );

          const formatedSuggestions = await Promise.all(
            rawSuggestions.map(async (suggestion) => {
              const place = suggestion.placePrediction.toPlace();

              await place.fetchFields({
                fields: ["displayName", "formattedAddress"],
              });

              return {
                place_id: place.id,
                displayName: place.displayName,
                formattedAddress: place.formattedAddress,
                description: `${place.displayName} ${place.formattedAddress}`,
              };
            })
          );

          setPredictions(formatedSuggestions);
        } catch (error) {
          throw new Error(error);
        }
      };

      getSuggestions();
    } else {
      setPredictions([]);
    }
  }, [places, countryCode, inputValue, spot.name]);

  return (
    <Box
      className="autocomplete-container"
      sx={{ maxWidth: 300, margin: "auto", mt: 4 }}
    >
      <Autocomplete
        options={predictions}
        getOptionLabel={(option) => option.description}
        onChange={onChange}
        name="googleId"
        noOptionsText="type name to see suggestions"
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                {option.displayName}
              </Box>
              <Box
                component="span"
                sx={{ color: "text.secondary", fontSize: "0.875rem" }}
              >
                {option.formattedAddress}
              </Box>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Place name"
            name="googleId"
            helperText="Search for the place name as it appears on Google Maps"
          />
        )}
        isOptionEqualToValue={(option, value) =>
          option.place_id === value.place_id
        }
      />
    </Box>
  );
};
